import React, { useState, useMemo } from "react";
import { Table, Button, Row, Col, Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import { useGetProductsQuery } from "../../slices/productsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetStocksQuery } from "../../slices/stocksApiSlice";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetTransfersQuery } from "../../slices/transfersApiSlice";
import { useGetOpeningStocksQuery } from "../../slices/openingStocksApiSlice";

const ReportStockScreen = () => {
  const [filter, setFilter] = useState("");

  // Queries
  const { pageNumber } = useParams();
  const { data: prods, isLoading: loadingP, error: errP } = useGetProductsQuery({ pageNumber });
  const { data: supp, isLoading: loadingS, error: errS } = useGetSuppliesQuery();
  const { data: stk, isLoading: loadingStk, error: errStk } = useGetStocksQuery();
  const { data: sales, isLoading: loadingSale, error: errSale } = useGetSalesQuery();
  const { data: trans, isLoading: loadingT, error: errT } = useGetTransfersQuery();
  const { data: opens, isLoading: loadingOS, error: errOS } = useGetOpeningStocksQuery();

  const loading = loadingP || loadingS || loadingStk || loadingSale || loadingT || loadingOS;
  const error = errP || errS || errStk || errSale || errT || errOS;

  // Determine earliest stock date for stock calculations
  const oldestStockDate = useMemo(() => {
    if (!stk?.stocks?.length) return "";
    return stk.stocks.reduce((oldest, s) => {
      return !oldest || new Date(s.date) < new Date(oldest) ? s.date : oldest;
    }, "");
  }, [stk]);

  // Compute mapped quantities
  const { openingMap, openingPriceMap, supplyMap, stockMap, stockPriceMap, soldMap, transferMap, priceMap } =
    useMemo(() => {
      const openingMap = new Map();
      const openingPriceMap = new Map();
      const supplyMap = new Map();
      const stockMap = new Map();
      const stockPriceMap = new Map();
      const soldMap = new Map();
      const transferMap = new Map();
      const priceMap = new Map();

      // Sum opening stock quantities and prices separately
      opens?.openingStocks.forEach(os =>
        os.openingStockItems.forEach(i => {
          openingMap.set(i.name, (openingMap.get(i.name) || 0) + i.qty);
          openingPriceMap.set(i.name, i.price);
        })
      );

      // Sum supplies, sales, transfers based on product name
      supp?.supplies.forEach(s =>
        s.supplyItems.forEach(i => supplyMap.set(i.name, (supplyMap.get(i.name) || 0) + i.qty))
      );
      sales?.sales.forEach(s =>
        s.saleItems.forEach(i => soldMap.set(i.name, (soldMap.get(i.name) || 0) + i.qty))
      );
      trans?.transfers.forEach(t =>
        t.transferItems.forEach(i => transferMap.set(i.name, (transferMap.get(i.name) || 0) + i.qty))
      );

      // Get product prices (Cur Price)
      prods?.products.forEach(p => priceMap.set(p.name, p.price));

      // Filter stocks based on earliest stock date **AND** matching product name
      stk?.stocks
        .filter(s => s.date === oldestStockDate)
        .forEach(s =>
          s.stockItems.forEach(i => {
            stockMap.set(i.name, (stockMap.get(i.name) || 0) + i.qty);
            stockPriceMap.set(i.name, i.price);
          })
        );

      return { openingMap, openingPriceMap, supplyMap, stockMap, stockPriceMap, soldMap, transferMap, priceMap };
    }, [opens, supp, stk, sales, trans, prods, oldestStockDate]);

  // Generate report rows
  const reportRows = useMemo(() => {
    if (!prods?.products) return [];

    return prods.products
      .filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
      .map(prod => {
        const openingQty = openingMap.get(prod.name) || 0;
        const opPrice = openingPriceMap.get(prod.name) || 0;

        const stkQty = stockMap.get(prod.name) || 0;
        const stPrice = stockPriceMap.get(prod.name) || 0;

        const supQty = supplyMap.get(prod.name) || 0;
        const soldQty = soldMap.get(prod.name) || 0;
        const trQty = transferMap.get(prod.name) || 0;
        const balQty = stkQty + supQty - soldQty - trQty;

        const curPrice = priceMap.get(prod.name) || 0;
        const incPrice = curPrice - opPrice;
        const balVal = balQty * curPrice;
        const opVal = opPrice * openingQty;
        const stVal = stPrice * stkQty;

        return {
          _id: prod._id,
          name: prod.name,
          openingQty,
          opPrice,
          stkQty,
          stPrice,
          supQty,
          soldQty,
          trQty,
          balQty,
          curPrice,
          incPrice,
          balVal,
          opVal,
          stVal,
        };
      });
  }, [prods, filter, openingMap, openingPriceMap, supplyMap, stockMap, stockPriceMap, soldMap, transferMap, priceMap]);

  // Export to Excel
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportRows.map(r => ({
      Product: r.name,
      "Opening QTY": r.openingQty,
      "Op Price": r.opPrice,
      "Stock QTY": r.stkQty,
      "St Price": r.stPrice,
      "Supplied QTY": r.supQty,
      "Sold QTY": r.soldQty,
      "Transferred QTY": r.trQty,
      "Balance QTY": r.balQty,
      "Cur Price": r.curPrice,
      "Inc Price": r.incPrice,
      "Balance Val": r.balVal.toFixed(2),
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), "report_stock.xlsx");
  };
  // Grand Total of Balance Value
  const grandTotalBalanceValue = reportRows.reduce((sum, row) => sum + row.balVal, 0).toFixed(2);
  const grandTotalOpValue = reportRows.reduce((sum, row) => sum + row.opVal, 0).toFixed(2);
  const grandTotalStValue = reportRows.reduce((sum, row) => sum + row.stVal, 0).toFixed(2);


  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Report Stock</h1></Col>
      </Row>
      {/* Grand Total */}
      <Row className="mb-3">
        <Col md={6}><h4>Balance Value: ${grandTotalBalanceValue}</h4></Col>
        <Col md={6}><h4>OPening Value: ${grandTotalOpValue}</h4></Col>
        <Col md={6}><h4>Stock Value: ${grandTotalStValue}</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={8}>
          <Form.Control type="text" placeholder="Filter by product" value={filter} onChange={e => setFilter(e.target.value)} />
        </Col>
        <Col md={4} className="text-end">
          <Button variant="success" onClick={handleExportExcel}>Export to Excel</Button>
        </Col>
      </Row>

      {!loading && !error && (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Product</th>
              <th>Op QTY</th>
              <th>Op Price</th>
              <th>Stock QTY</th>
              <th>St Price</th>
              <th>Sup QTY</th>
              <th>Sold QTY</th>
              <th>Transf QTY</th>
              <th>Bal QTY</th>
              <th>Cur Price</th>
              <th>Inc Price</th>
              <th>Bal Val</th>
              <th>OP Val</th>
              <th>ST Val</th>
            </tr>
          </thead>
          <tbody>{reportRows.map(r => <tr key={r._id}>{Object.values(r).map((value, idx) => <td key={idx}>{value}</td>)}</tr>)}</tbody>
        </Table>
      )}
    </>
  );
};

export default ReportStockScreen;